/**
 * Correlation identities, matching MobileCh3's src/lib/pushMlog.ts.
 *
 * Its own module, importing NOTHING, for the same reason pushMlog.ts does:
 * services/logger.ts sits in a circular import cycle (logger -> store -> ...
 * -> audioPageLogger -> logger) that makes it unloadable in a unit test. That
 * cycle predates this file. Keeping the identities out of it means the part
 * with actual rules is testable, and the cycle is somebody else's problem on
 * a different day.
 *
 * Both clients ship ECS to the same logstash, so a call that starts in the
 * backend can be followed into whichever client handled it - but only if they
 * agree on the key names. These four are that agreement:
 *
 *   trace_id    minted by channels (MatrixCallPushNotifier) and carried
 *               outward; the join key across repos
 *   call_id     the logical call
 *   attempt_id  one connection attempt - callId alone collapses retries
 *   session_id  one page load, so a reload is distinguishable from a
 *               continuous session
 *
 * session_id is stamped on every entry automatically; the call-scoped three
 * come from setCallContext, so existing call sites need no changes.
 */
const randomId = (): string => {
  const bytes = new Uint8Array(8);
  // Non-secret correlation id: crypto when available, Math.random in the
  // insecure-origin case rather than failing to log at all.
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
};

/** One page load. A reload mints a new one, which is the point. */
const SESSION_ID = randomId();

export interface CallCorrelation {
  trace_id?: string;
  call_id?: string;
  attempt_id?: string;
}

let callContext: CallCorrelation = {};

/** Merge semantics: set trace_id on call start, add attempt_id later. */
export const setCallContext = (next: CallCorrelation): void => {
  callContext = { ...callContext, ...next };
};

/** New attempt on the same call: fresh attempt_id, trace and call preserved. */
export const nextCallAttempt = (): string => {
  const attempt_id = randomId();
  callContext = { ...callContext, attempt_id };
  return attempt_id;
};

/** Call over - later entries must not inherit a finished call's identities. */
export const clearCallContext = (callId?: string): void => {
  if (callId !== undefined && callContext.call_id !== callId) return;
  callContext = {};
};

export const getSessionId = (): string => SESSION_ID;
export const getCallContext = (): CallCorrelation => ({ ...callContext });


/** Snapshot for the log entry builder. */
export const getCallContextSnapshot = (): CallCorrelation => callContext;

export { SESSION_ID };

/** Begin once per engine call; late backend metadata enriches the same attempt. */
export const beginCallContext = (callId: string, traceId?: string): void => {
  if (callContext.call_id !== callId) {
    callContext = {call_id: callId, trace_id: traceId ?? callId};
    nextCallAttempt();
  } else if (traceId) {
    callContext = {...callContext, trace_id: traceId};
  }
};
